import { type MbscResource } from '@mobiscroll/react';
import { addDays, eachDayOfInterval, endOfDay, type Interval, isWithinInterval, startOfDay } from 'date-fns';

import { type Ablauf, type AkaVeranstaltungId, Zeitraum } from '../../../../../dtos';
import { calendarColors } from '../../../../../theme/colorPalette';
import { getIntervalAlignedToStartAndEndOfDay } from '../../../../../utils';
import { type CalendarEvent } from '../CalendarEvent';
import { type CalendarResourceType } from '../calendarResourceTypes';
import {
  type EventCreationStrategy,
  type IntervalWithOptionalVeranstaltung,
} from './AbstractCalendarResource.types.js';

export type SuggestedEvents = {
  tage: { zeitraum: Zeitraum; expertenSapIds: number[]; gruppenraumSapIds?: number[] }[];
  seminarraumSapId?: number;
};

/**
 * Represents one blockable Ressource in our system, and one row in the calendar.
 */
export abstract class AbstractCalendarResource implements MbscResource {
  /**
   * Type of the Ressource. Used to group resources in the calendar.
   */
  public abstract readonly type: CalendarResourceType;

  /**
   * Unique row identifier. Consists of prefix plus SapId of the underlying Ressource.
   * Needed by Mobiscroll -> Don't rename.
   *
   * @example For a Raum, this would be 'r-60000503'.
   */
  public abstract readonly id: string;

  /**
   * Specifies the name of the calendar row.
   * Needed by Mobiscroll -> Don't rename.
   */
  public abstract readonly name: string;

  /**
   * Additional information about the Ressource, shown below the name.
   * E.g. for Seminarraum: short description of the room, size, capacity
   */
  public subtext: string | undefined;

  /**
   * Defines how new events are created.
   *
   * @see EventCreationStrategy
   */
  public abstract readonly eventCreationStrategy: EventCreationStrategy;

  /**
   * Background color of the row in the calendar.
   * Used to visually distinguish different resource types.
   */
  public background?: string = calendarColors.notBookable; // not bookable background by default

  /**
   * Turns pre-existing Blockungen of the underlying Ressource into CalendarEvents.
   * See AbstractCalendarEvent for more info.
   */
  public abstract createFixedEventsFromBlockungen(akaVeranstaltungId: AkaVeranstaltungId | null): CalendarEvent[];

  /**
   * Turns pre-existing Blockungen that belong to the given akaVeranstaltungId into CalendarEvents.
   */
  public abstract createPendingEventsFromBlockungen(akaVeranstaltungId: AkaVeranstaltungId): CalendarEvent[];

  /**
   * Turns suggested events into pending CalendarEvents for the underlying Ressource.
   */
  public abstract createPendingEventsFromSuggestions(
    suggestedEvents: SuggestedEvents,
    akaVeranstaltungId: AkaVeranstaltungId | null,
  ): CalendarEvent[];

  /**
   * Used when putting in new Blockungen for the underlying Ressource.
   */
  public createNewCalendarEvent(date: Date, daySpan: number): CalendarEvent {
    return this.createNewCalendarEventByInterval({
      start: startOfDay(date),
      end: endOfDay(addDays(date, daySpan - 1)),
    });
  }

  public abstract createNewCalendarEventByInterval(interval: Interval<Date, Date>): CalendarEvent;

  public abstract getRessourceSapId(): number;

  /**
   * Determines whether the resource is bookable at the given date
   */
  public abstract isBookableAt(date: Date): boolean;

  public isBookedBy(date: Date, akaVeranstaltungId: AkaVeranstaltungId): boolean {
    return this.getBlockungen().some(
      (blockung) =>
        blockung.veranstaltung?.akaVeranstaltungId === akaVeranstaltungId &&
        blockung.zeitraeume.some((zeitraum) => isWithinInterval(date, getIntervalAlignedToStartAndEndOfDay(zeitraum))),
    );
  }

  protected abstract getBlockungen(): IntervalWithOptionalVeranstaltung[];

  protected getBlockungStart(zeitraeume: Ablauf): Date {
    return zeitraeume.map((tag) => tag.start).reduce((previous, current) => (current < previous ? current : previous));
  }

  protected getBlockungEnd(zeitraeume: Ablauf): Date {
    return zeitraeume.map((tag) => tag.end).reduce((previous, current) => (current > previous ? current : previous));
  }

  protected createZeitraeumeByInterval(interval: Interval<Date, Date>): Ablauf {
    return eachDayOfInterval(interval).map((date) => ({
      start: date,
      end: date,
    }));
  }
}
